// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (c) 2026 Password Manager contributors
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useState, useCallback } from "react";
import { SuperadminService } from "@/services/superadmin";
import { AiImportFilters } from "@/components/superadmin/ai-imports/AiImportFilters";
import { AiImportTable } from "@/components/superadmin/ai-imports/AiImportTable";
import { AiImportDetail } from "@/components/superadmin/ai-imports/AiImportDetail";
import type { AiImportTrial } from "@/types/superadmin";

const STATUS_VALUES = ["all", "success", "failed"] as const;
type StatusFilter = (typeof STATUS_VALUES)[number];

export interface SearchParams {
  page: number;
  status: StatusFilter;
}

export const Route = createFileRoute("/superadmin/ai-imports")({
  component: AiImportsPage,
  validateSearch: (search: Record<string, unknown>): SearchParams => ({
    page: Number(search.page) || 1,
    status: STATUS_VALUES.includes(search.status as StatusFilter)
      ? (search.status as StatusFilter)
      : "all",
  }),
});

function AiImportsPage() {
  const { t } = useTranslation("superadmin");
  const navigate = useNavigate();
  const params = Route.useSearch();

  const updateParams = useCallback(
    (updates: Partial<SearchParams>) => {
      navigate({
        to: "/superadmin/ai-imports",
        search: { ...params, ...updates },
        replace: true,
      });
    },
    [navigate, params],
  );

  // Query
  const { data, isLoading } = useQuery({
    queryKey: ["superadmin", "aiImportTrials", params],
    queryFn: () =>
      SuperadminService.listAiImportTrials({
        page: params.page,
        limit: 20,
        status: params.status,
      }),
  });

  // Detail sheet state
  const [selectedTrial, setSelectedTrial] = useState<AiImportTrial | null>(
    null,
  );
  const [detailOpen, setDetailOpen] = useState(false);

  const handleViewDetail = useCallback((trial: AiImportTrial) => {
    setSelectedTrial(trial);
    setDetailOpen(true);
  }, []);

  const handleFilterChange = useCallback(
    (key: string, value: string) => {
      updateParams({ [key]: value, page: 1 });
    },
    [updateParams],
  );

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("aiImports.title")}</h1>
        <p className="text-muted-foreground mt-1">
          {t("aiImports.description")}
        </p>
      </div>

      <AiImportFilters
        status={params.status}
        onFilterChange={handleFilterChange}
      />

      <AiImportTable
        trials={data?.trials ?? []}
        pagination={data?.pagination}
        isLoading={isLoading}
        onPageChange={(page) => updateParams({ page })}
        onViewDetail={handleViewDetail}
      />

      <AiImportDetail
        trial={selectedTrial}
        open={detailOpen}
        onOpenChange={setDetailOpen}
      />
    </div>
  );
}
